/**
 * useBusinessAccountUsers Hook
 *
 * Fetches the list of users with access to the current business account.
 *
 * @module features/users/hooks/useBusinessAccountUsers
 */

import { useQuery } from '@tanstack/react-query';
import { listBusinessAccountUsers } from '@/features/users/services/businessAccountUsers';
import type {
  BusinessAccountUsersResponse,
  ListUsersParams,
} from '@/features/users/types/users.types';

export const BUSINESS_ACCOUNT_USERS_QUERY_KEY = [
  'business-account-users',
] as const;

/**
 * Hook for fetching business account users
 *
 * @param params - Pagination parameters
 * @returns TanStack Query result with paginated users
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useBusinessAccountUsers({ offset: 0, limit: 10 });
 *
 * if (isLoading) return <Spinner />;
 * if (error) return <Error />;
 *
 * return <Table dataSource={data.content} />;
 * ```
 */
export function useBusinessAccountUsers(params: ListUsersParams = {}) {
  return useQuery<BusinessAccountUsersResponse, Error>({
    queryKey: [...BUSINESS_ACCOUNT_USERS_QUERY_KEY, params],
    queryFn: () => listBusinessAccountUsers(params),
    retry: (failureCount, error) => {
      // Never retry 401 – the user simply lacks permission for this endpoint
      if (
        'response' in error &&
        (error as { response?: { status?: number } }).response?.status === 401
      ) {
        return false;
      }
      return failureCount < 1;
    },
    refetchOnWindowFocus: false,
  });
}
